import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Switch,
  FormControlLabel,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Backdrop,
  Divider,
} from '@mui/material';
import { Fade } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LockIcon from '@mui/icons-material/Lock';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ExtensionOffIcon from '@mui/icons-material/ExtensionOff';
import Info from './Info';
import { useAdminLanguage } from './AdminI18n';

const FEATURE_OPTIONS = [
  { key: 'listen', label: 'GuardIA Listen (écriture vocale)' },
  { key: 'vision', label: 'GuardIA Vision (description d\'image)' },
  { key: 'audio', label: 'GuardIA Audio (transcription de fichier audio)' },
  { key: 'synthese', label: 'GuardIA Synthèse (synthèse des faits)' },
  // { key: 'check', label: 'GuardIA Check (correction de rubrique)' },
];

const normalizeGuardiaSettings = (settings) => {
  if (!settings || typeof settings !== 'object') {
    return settings;
  }

  const guardiaOn = typeof settings.guardiaOn === 'boolean' ? settings.guardiaOn : false;
  const sourceFeatures = settings.features && typeof settings.features === 'object'
    ? settings.features
    : {};

  const features = FEATURE_OPTIONS.reduce((acc, option) => {
    if (typeof sourceFeatures[option.key] === 'boolean') {
      acc[option.key] = sourceFeatures[option.key];
    } else {
      acc[option.key] = guardiaOn;
    }
    return acc;
  }, {});

  return {
    ...settings,
    guardiaOn,
    features
  };
};

const GuardiaAdmin = () => {
  const { t } = useAdminLanguage();
  const [guardiaSettings, setGuardiaSettings] = useState(null);
  const [guardiaAccess, setGuardiaAccess] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('authToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Vérifier l'accès GuardIA via la licence
        const accessResponse = await fetch('/api/guardiasettings/access', { headers: getAuthHeaders() });
        if (!accessResponse.ok) throw new Error(`Erreur accès ${accessResponse.status}`);
        const accessData = await accessResponse.json();
        
        setHasAccess(accessData.hasAccess);
        setAccessMessage(accessData.reason);

        // Récupérer les paramètres GuardIA
        const settingsResponse = await fetch('/api/guardiasettings', { headers: getAuthHeaders() });
        if (!settingsResponse.ok) throw new Error(`Erreur paramètres ${settingsResponse.status}`);
        const settingsData = await settingsResponse.json();
        
        setGuardiaSettings(normalizeGuardiaSettings(settingsData));

      } catch (err) {
        console.error('Erreur lors de la récupération des données GuardIA :', err);
        setError('Une erreur est survenue lors du chargement des données GuardIA.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSwitchChange = async (event) => {
    if (!hasAccess) {
      return; // Ne rien faire si pas d'accès
    }

    const newStatus = event.target.checked;
    setUpdating(true);
    setMessage('');
    setError(null);

    try {
      const response = await fetch('/api/guardiasettings/status', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ guardiaOn: newStatus }),
      });
      
      const data = await response.json();

      if (response.ok) {
        setGuardiaSettings(normalizeGuardiaSettings(data.settings));
        setMessage(`GuardIA ${newStatus ? 'activé' : 'désactivé'} avec succès.`);
      } else {
        setError(data.error || 'Erreur lors de la mise à jour du statut GuardIA.');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour :', err);
      setError('Erreur lors de la communication avec le serveur.');
    } finally {
      setUpdating(false);
    }
  };

  const handleFeatureSwitchChange = (featureKey) => async (event) => {
    if (!hasAccess || !isInstalled || !guardiaSettings?.guardiaOn) {
      return;
    }

    const newStatus = event.target.checked;

    if (featureKey === 'resume' && newStatus && !guardiaSettings?.features?.audio) {
      setError('GuardIA Resume nécessite GuardIA Audio activé.');
      return;
    }

    setUpdating(true);
    setMessage('');
    setError(null);

    try {
      const response = await fetch(`/api/guardiasettings/features/${featureKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ enabled: newStatus }),
      });

      const data = await response.json();
      if (response.ok) {
        setGuardiaSettings(normalizeGuardiaSettings(data.settings));
        setMessage(`${featureKey} ${newStatus ? 'activé' : 'désactivé'} avec succès.`);
      } else {
        setError(data.error || 'Erreur lors de la mise à jour de la fonctionnalité GuardIA.');
      }
    } catch (err) {
      console.error('Erreur lors de la mise à jour d\'une fonctionnalité GuardIA :', err);
      setError('Erreur lors de la communication avec le serveur.');
    } finally {
      setUpdating(false);
    }
  };

  const isInstalled = guardiaSettings?.guardiaInstalled === true;

  const getStatusColor = () => {
    if (!hasAccess) return 'error';
    if (!isInstalled) return 'default';
    return guardiaSettings?.guardiaOn ? 'success' : 'default';
  };

  const getStatusIcon = () => {
    if (!hasAccess) return <LockIcon />;
    if (!isInstalled) return <ExtensionOffIcon />;
    return guardiaSettings?.guardiaOn ? <CheckCircleIcon /> : <SmartToyIcon />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in={!loading} timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion de GuardIA')}
          </Typography>

          <Info type="guardia" />

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* Overlay pour bloquer l'interface si pas d'accès ou addon non installé */}
              {(!hasAccess || !isInstalled) && (
                <Backdrop
                  open={true}
                  sx={{
                    position: 'absolute',
                    zIndex: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    borderRadius: 1,
                  }}
                />
              )}
              
              <Grid container spacing={3}>
                {/* Section Contrôle GuardIA */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    opacity: (hasAccess && isInstalled) ? 1 : 0.5,
                    transition: 'opacity 0.3s'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getStatusIcon()}
                        {t('Contrôle GuardIA')}
                      </Typography>
                      
                      <FormControlLabel
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={guardiaSettings?.guardiaOn || false}
                            onChange={handleSwitchChange}
                            disabled={!hasAccess || !isInstalled || updating}
                            color={getStatusColor()}
                          />
                        }
                        label={
                          <Typography sx={{ mr: 2 }}>
                            {guardiaSettings?.guardiaOn ? t('GuardIA Activé') : t('GuardIA Désactivé')}
                          </Typography>
                        }
                        sx={{
                          mt: 2,
                          width: '100%',
                          m: 0,
                          justifyContent: 'space-between',
                          '& .MuiFormControlLabel-label': { flex: 1 }
                        }}
                      />

                      {guardiaSettings?.guardiaOn && (
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            {t('Fonctionnalités GuardIA')}
                          </Typography>

                          {FEATURE_OPTIONS.map((option, idx) => {
                            const isResume = option.key === 'resume';
                            const isAudioEnabled = Boolean(guardiaSettings?.features?.audio);
                            const disabled = !hasAccess
                              || !isInstalled
                              || updating
                              || !guardiaSettings?.guardiaOn
                              || (isResume && !isAudioEnabled);

                            return (
                              <Box key={option.key}>
                                <FormControlLabel
                                  labelPlacement="start"
                                  control={
                                    <Switch
                                      checked={guardiaSettings?.features?.[option.key] || false}
                                      onChange={handleFeatureSwitchChange(option.key)}
                                      disabled={disabled}
                                      color={guardiaSettings?.features?.[option.key] ? 'success' : 'default'}
                                    />
                                  }
                                  label={
                                    <Typography sx={{ mr: 2, opacity: (isResume && !isAudioEnabled) ? 0.6 : 1 }}>
                                      {t(option.label)}
                                    </Typography>
                                  }
                                  sx={{
                                    width: '100%',
                                    m: 0,
                                    justifyContent: 'space-between',
                                    pl: option.parent ? 2 : 0,
                                    py: 0.25,
                                    '& .MuiFormControlLabel-label': { flex: 1 }
                                  }}
                                />
                                {idx < FEATURE_OPTIONS.length - 1 && <Divider sx={{ my: 0.25 }} />}
                              </Box>
                            );
                          })}

                          {!guardiaSettings?.features?.audio && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, opacity: 0.75 }}>
                              {t('GuardIA Resume est disponible uniquement si GuardIA Audio est activé.')}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {updating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">{t('Mise à jour en cours...')}</Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>

                  {/* Messages d'état */}
                  {!isInstalled && hasAccess && (
                    <Alert 
                      severity="warning" 
                      sx={{ mt: 2 }} 
                      icon={<ExtensionOffIcon />}
                    >
                      {t('L\'addon GuardIA n\'est pas installé sur ce serveur.')}
                    </Alert>
                  )}

                  {!hasAccess && (
                    <Alert 
                      severity="error" 
                      sx={{ mt: 2 }} 
                      icon={<LockIcon />}
                    >
                      {t(accessMessage)}
                    </Alert>
                  )}

                  {message && (
                    <Alert severity="success" sx={{ mt: 2 }} icon={<CheckCircleIcon />}>
                      {t(message)}
                    </Alert>
                  )}
                  
                  {error && (
                    <Alert severity="error" sx={{ mt: 2 }} icon={<ErrorIcon />}>
                      {t(error)}
                    </Alert>
                  )}
                </Grid>

                {/* Section Informations GuardIA */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    opacity: (hasAccess && isInstalled) ? 1 : 0.5,
                    transition: 'opacity 0.3s'
                  }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {t('Informations GuardIA')}
                      </Typography>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('Installation')} :</strong>{' '}
                          <span style={{ color: isInstalled ? 'green' : 'orange' }}>
                            {isInstalled ? t('Installé') : t('Non installé')}
                          </span>
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('Statut')} :</strong>{' '}
                          <span style={{ 
                            color: !hasAccess ? 'red' : (!isInstalled ? 'grey' : (guardiaSettings?.guardiaOn ? 'green' : 'orange'))
                          }}>
                            {!hasAccess 
                              ? t('Non autorisé')
                              : (!isInstalled ? t('Addon absent') : (guardiaSettings?.guardiaOn ? t('Actif') : t('Inactif')))
                            }
                          </span>
                        </Typography>
                        
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('Version')} :</strong>{' '}
                          {guardiaSettings?.guardiaVersion || t('Non définie')}
                        </Typography>

                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('Accès licence')} :</strong>{' '}
                          <span style={{ color: hasAccess ? 'green' : 'red' }}>
                            {hasAccess ? t('Autorisé') : t('Non autorisé')}
                          </span>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Fade>
    </Container>
  );
};

export default GuardiaAdmin;
